import { useEffect, useMemo, useState, useContext } from "react";
import NotificationContext from "../context/NotificationContext";
import FeeImagePlaceholder from "./FeeImagePlaceholder";
import { isSafeFeeProofDisplayUrl } from "../utils/feeProofUpload";
import { useMutationLock } from "../hooks/useMutationLock";
import * as feeTransactionsApi from "../api/feeTransactions";
import * as registrationsApi from "../api/registrations";

const toDateInputValue = (value) => {
  if (!value) return "";
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return text;
};

function FeeVerificationModal({
  selectedRegistration,
  setShowfeeImageModal,
  transactionId,
  amountPaid,
  feeimage,
  paymentDate,
  setPaymentDate,
  setTransactionId,
  setAmountPaid,
  verifyFeePayment,
  feePaymentProofId: _feePaymentProofId,
  feeTransactionId,
  isExistingPayment,
  canEdit = false,
  onSuccess,
}) {
  const notificationCtx = useContext(NotificationContext);
  const { busy: submitting, run: runSubmit } = useMutationLock();

  const registrationKey =
    selectedRegistration.id || selectedRegistration.registrationId;
  const [courses, setCourses] = useState([]);
  const [selectedPeriods, setSelectedPeriods] = useState({});
  const [allocationAmounts, setAllocationAmounts] = useState({});
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [existingTransaction, setExistingTransaction] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(isExistingPayment);

  useEffect(() => {
    setSelectedPeriods({});
    setAllocationAmounts({});
    setLoadingPeriods(true);

    let active = true;
    const loadPeriods = async () => {
      try {
        const response = await feeTransactionsApi.feePeriods(registrationKey);
        if (active) setCourses(response.data);
      } catch (error) {
        console.error("Error fetching fee periods:", error);
        notificationCtx.show({
          message: "Failed to fetch fee periods.",
          stat: "error",
        });
      } finally {
        if (active) setLoadingPeriods(false);
      }
    };
    loadPeriods();
    return () => {
      active = false;
    };
  }, [registrationKey, notificationCtx]);

  useEffect(() => {
    if (!isExistingPayment || !registrationKey) {
      setExistingTransaction(null);
      setLoadingExisting(false);
      return;
    }

    let active = true;
    const loadExistingTransaction = async () => {
      setLoadingExisting(true);
      try {
        const response =
          await feeTransactionsApi.getByRegistration(registrationKey);
        const transactions = response.data || [];
        const matched =
          (feeTransactionId &&
            transactions.find(
              (item) => String(item.id) === String(feeTransactionId),
            )) ||
          null;
        if (!active) return;
        if (!matched) {
          notificationCtx.show({
            message: "Could not find this fee transaction.",
            stat: "error",
          });
          setExistingTransaction(null);
          return;
        }
        setExistingTransaction(matched);
        setTransactionId(matched.transactionId || "");
        setAmountPaid(
          matched.amountPaid == null ? "" : String(matched.amountPaid),
        );
        setPaymentDate(toDateInputValue(matched.paymentDate));
      } catch (error) {
        console.error("Error fetching transaction:", error);
        notificationCtx.show({
          message: "Get Transaction Failed",
          stat: "error",
        });
      } finally {
        if (active) setLoadingExisting(false);
      }
    };
    loadExistingTransaction();
    return () => {
      active = false;
    };
  }, [
    isExistingPayment,
    registrationKey,
    feeTransactionId,
    notificationCtx,
    setAmountPaid,
    setPaymentDate,
    setTransactionId,
  ]);

  const [editingExistingPayment, setEditingExistingPayment] = useState(false);
  const [showWriteOffControls, setShowWriteOffControls] = useState(false);

  useEffect(() => {
    setShowWriteOffControls(false);
    setEditingExistingPayment(false);
  }, [registrationKey, isExistingPayment]);

  const existingAllocations = useMemo(() => {
    const amounts = {};
    for (const item of existingTransaction?.items || []) {
      amounts[item.feePeriodId] = Number(item.amountAllocated);
    }
    return amounts;
  }, [existingTransaction]);

  useEffect(() => {
    if (!existingTransaction || loadingPeriods || courses.length === 0) return;
    const nextSelected = {};
    const nextAmounts = {};
    for (const item of existingTransaction.items || []) {
      const course = courses.find(
        (entry) => String(entry.courseId) === String(item.courseId),
      );
      const period = course?.periods?.find(
        (entry) => String(entry.feePeriodId) === String(item.feePeriodId),
      );
      if (!period) continue;
      if (!nextSelected[course.courseId]) nextSelected[course.courseId] = [];
      nextSelected[course.courseId].push(period);
      nextAmounts[period.feePeriodId] = Number(item.amountAllocated);
    }
    setSelectedPeriods(nextSelected);
    setAllocationAmounts(nextAmounts);
  }, [existingTransaction, loadingPeriods, courses]);

  const selectedItems = useMemo(
    () => Object.values(selectedPeriods).flat(),
    [selectedPeriods],
  );

  /** Chronological (earliest month first) under Fee coverage. */
  const coursesSorted = useMemo(
    () =>
      courses.map((course) => ({
        ...course,
        periods: [...(course.periods || [])].sort((a, b) => {
          if (Number(a.year) !== Number(b.year)) {
            return Number(a.year) - Number(b.year);
          }
          return Number(a.month) - Number(b.month);
        }),
      })),
    [courses],
  );
  const remainingForPeriod = (period) =>
    Number(period.amountRemaining) +
    (existingAllocations[period.feePeriodId] || 0);
  const totalAllocated = selectedItems.reduce(
    (sum, period) =>
      sum +
      Number(
        allocationAmounts[period.feePeriodId] ?? remainingForPeriod(period),
      ),
    0,
  );
  const parseFee = (fee) =>
    Number(String(fee ?? "").replace(/[^0-9]/g, "")) || 0;
  const parseDiscount = (discount) =>
    Number(String(discount ?? "0").replace(/[^0-9.]/g, "")) || 0;

  const activeCourses =
    selectedRegistration.courses?.filter(
      (course) => course.status === "enrolled" || course.status === "pending",
    ) || [];
  // Same formula as AdminRegistrations Fee column: net of discount (PKR amount).
  const monthlyFee = activeCourses.reduce((total, course) => {
    const fee = parseFee(course.fee);
    const discount = parseDiscount(course.discount);
    return total + Math.max(0, Math.round(fee - discount));
  }, 0);

  // Fee Coverage uses fee-periods API (no discount). Join pricing from the
  // registration row already loaded in the table — no extra backend call.
  const coursePricingById = useMemo(() => {
    const map = {};
    for (const course of selectedRegistration.courses || []) {
      const fee = parseFee(course.fee);
      const discount = parseDiscount(course.discount);
      map[String(course.courseId ?? course.id)] = {
        fee,
        discount,
        net: Math.max(0, Math.round(fee - discount)),
      };
    }
    return map;
  }, [selectedRegistration.courses]);

  const close = () => {
    setShowfeeImageModal(false);
  };

  const togglePeriod = (courseId, period) => {
    const remaining = remainingForPeriod(period);
    setSelectedPeriods((current) => {
      const periods = current[courseId] || [];
      const exists = periods.some(
        (item) => item.feePeriodId === period.feePeriodId,
      );
      return {
        ...current,
        [courseId]: exists
          ? periods.filter((item) => item.feePeriodId !== period.feePeriodId)
          : [...periods, period],
      };
    });
    setAllocationAmounts((current) => ({
      ...current,
      [period.feePeriodId]: remaining,
    }));
  };

  const feeItemsPayload = () =>
    selectedItems.map((period) => ({
      feePeriodId: period.feePeriodId,
      courseId: period.courseId,
      amountAllocated:
        allocationAmounts[period.feePeriodId] ?? remainingForPeriod(period),
    }));

  const submit = () => {
    if (!canEdit) return;
    if (selectedItems.length === 0) return;
    runSubmit(async () => {
      await verifyFeePayment(feeItemsPayload());
    });
  };

  const updateExistingPayment = () => {
    if (!canEdit) return;
    if (!existingTransaction?.id || selectedItems.length === 0) return;
    runSubmit(async () => {
      try {
        const response = await feeTransactionsApi.update(
          existingTransaction.id,
          {
            transactionId,
            registrationId: registrationKey,
            amountPaid,
            paymentDate,
            feeItems: feeItemsPayload(),
          },
        );
        if (response.data.message === "Transaction Updated Successfully") {
          notificationCtx.show({
            message: "Fee payment updated successfully!",
            stat: "success",
          });
          setShowfeeImageModal(false);
          if (typeof onSuccess === "function") {
            await onSuccess();
          }
        }
      } catch (error) {
        console.error("Error updating transaction:", error);
        if (error.response?.data?.errorCode === "DUPLICATE_TRANSACTION") {
          notificationCtx.show({
            message:
              error.response.data.message ||
              "A duplicate transaction id already exists.",
            stat: "error",
          });
        } else {
          notificationCtx.show({
            message:
              error.response?.data?.message || "Failed to update fee payment.",
            stat: "error",
          });
        }
      }
    });
  };

  const cardStyle = {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "20px",
    boxShadow: "0 3px 12px rgba(0,0,0,0.07)",
  };
  const labelStyle = {
    fontSize: "11px",
    color: "#85808a",
    marginBottom: "3px",
    letterSpacing: "0.4px",
  };

  return (
    <div
      className="modal fade show"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.65)",
        zIndex: 10500,
        padding: "20px",
      }}
      tabIndex="-1"
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered"
        style={{
          width: "100%",
          maxWidth: "100vw",
          margin: 0,
        }}
      >
        <div
          className="modal-content"
          style={{
            backgroundColor: "#ffffff",
            border: "none",
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            maxHeight: "110vh",
          }}
        >
          <div
            className="modal-header"
            style={{
              backgroundColor: "#d5d1fd",
              border: "none",
              padding: "20px 24px",
              minHeight: "76px",
            }}
          >
            <div>
              <h5
                className="modal-title mb-1"
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#252333",
                }}
              >
                Fee Payment Verification
              </h5>
              <div
                style={{
                  fontSize: "13px",
                  color: "#4f4a60",
                  fontWeight: "500",
                }}
              >
                {selectedRegistration.name || "Student"}
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={close}
              aria-label="Close"
              style={{
                backgroundColor: "rgba(255,255,255,0.65)",
                borderRadius: "50%",
                padding: "10px",
                opacity: 0.85,
              }}
            />
          </div>

          <div
            className="modal-body"
            style={{ padding: "24px", overflowY: "auto" }}
          >
            <div className="row g-4">
              <div className="col-lg-5">
                <div
                  style={{
                    ...cardStyle,
                    height: "100%",
                    minHeight: "460px",
                    padding: "18px",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6
                        style={{
                          margin: 0,
                          fontWeight: "700",
                          color: "#34313a",
                        }}
                      >
                        Payment Receipt
                      </h6>
                      <small style={{ color: "#77727d", fontSize: "12px" }}>
                        Review the submitted payment proof
                      </small>
                    </div>
                    <span
                      style={{
                        backgroundColor: "#f9e1be",
                        color: "#403a32",
                        borderRadius: "20px",
                        padding: "5px 10px",
                        fontSize: "11px",
                        fontWeight: "700",
                      }}
                    >
                      PAYMENT PROOF
                    </span>
                  </div>
                  <div
                    style={{
                      height: "calc(100% - 65px)",
                      minHeight: "355px",
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "15px",
                      overflow: "hidden",
                    }}
                  >
                    {isSafeFeeProofDisplayUrl(feeimage) ? (
                      <img
                        src={feeimage}
                        alt="Fee payment receipt"
                        referrerPolicy="no-referrer"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "410px",
                          objectFit: "contain",
                          borderRadius: "8px",
                          boxShadow: "0 4px 15px rgba(0,0,0,0.12)",
                          backgroundColor: "#fff",
                        }}
                      />
                    ) : (
                      <FeeImagePlaceholder minHeight={320} />
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-7">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div style={{ ...cardStyle, height: "100%" }}>
                      <h6
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.8px",
                          fontWeight: "700",
                          color: "#6c6875",
                          marginBottom: "16px",
                        }}
                      >
                        Student Information
                      </h6>
                      <div className="mb-3">
                        <div style={labelStyle}>NAME</div>
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: "600",
                            color: "#34313a",
                          }}
                        >
                          {selectedRegistration.name}
                        </div>
                      </div>
                      <div className="mb-3">
                        <div style={labelStyle}>PHONE</div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#34313a",
                          }}
                        >
                          {selectedRegistration.phone}
                        </div>
                      </div>
                      <div>
                        <div style={labelStyle}>ACTIVE COURSES</div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                          }}
                        >
                          {activeCourses.map((course) => (
                            <span
                              key={course.id}
                              style={{
                                backgroundColor: "#d5d1fd",
                                color: "#403b52",
                                borderRadius: "7px",
                                padding: "5px 9px",
                                fontSize: "12px",
                                fontWeight: "600",
                              }}
                            >
                              {course.title}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div style={{ ...cardStyle, height: "100%" }}>
                      <h6
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.8px",
                          fontWeight: "700",
                          color: "#6c6875",
                          marginBottom: "16px",
                        }}
                      >
                        Fee Summary
                      </h6>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span style={{ color: "#6c6875", fontSize: "13px" }}>
                          Monthly Fee
                        </span>
                        <strong style={{ color: "#34313a", fontSize: "15px" }}>
                          PKR {monthlyFee.toLocaleString()}
                        </strong>
                      </div>
                      {(() => {
                        const owed =
                          Number(selectedRegistration.owedTotal) || 0;
                        const credit =
                          Number(selectedRegistration.creditTotal) || 0;
                        return (
                          <>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span
                                style={{ color: "#6c6875", fontSize: "13px" }}
                              >
                                Amount Owed
                              </span>
                              <strong
                                style={{
                                  color: owed > 0 ? "#d65c5c" : "#34313a",
                                  fontSize: "16px",
                                }}
                              >
                                PKR {owed.toLocaleString()}
                              </strong>
                            </div>
                            {credit > 0 ? (
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span
                                  style={{ color: "#6c6875", fontSize: "13px" }}
                                >
                                  Prepaid Credit
                                </span>
                                <strong
                                  style={{ color: "#5b4f8a", fontSize: "14px" }}
                                >
                                  PKR {credit.toLocaleString()}
                                </strong>
                              </div>
                            ) : null}
                            <div
                              className="d-flex justify-content-between align-items-center"
                              style={{
                                paddingTop: "12px",
                                borderTop: "1px solid #eeeae5",
                              }}
                            >
                              <span
                                style={{ color: "#6c6875", fontSize: "13px" }}
                              >
                                Display Balance
                              </span>
                              <strong
                                style={{ color: "#34313a", fontSize: "14px" }}
                              >
                                {selectedRegistration.balance || "PKR 0"}
                              </strong>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="col-12">
                    <div style={cardStyle}>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                          <h6
                            style={{
                              margin: 0,
                              fontWeight: "700",
                              color: "#34313a",
                            }}
                          >
                            Payment Verification
                          </h6>
                          <small style={{ color: "#77727d", fontSize: "12px" }}>
                            {isExistingPayment
                              ? "Update the stored transaction details and course-month coverage."
                              : "Enter the transaction details before allocating the payment."}
                          </small>
                        </div>
                        <span
                          style={{
                            backgroundColor: isExistingPayment
                              ? "#bff1db"
                              : "#f9e1be",
                            color: isExistingPayment ? "#315d4b" : "#403a32",
                            borderRadius: "20px",
                            padding: "5px 10px",
                            fontSize: "11px",
                            fontWeight: "700",
                          }}
                        >
                          {isExistingPayment ? "VERIFIED" : "PENDING REVIEW"}
                        </span>
                      </div>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label
                            htmlFor="paymentDate"
                            className="form-label"
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#514d57",
                            }}
                          >
                            Payment Date
                          </label>
                          <input
                            id="paymentDate"
                            type="date"
                            value={toDateInputValue(paymentDate) || new Date().toISOString().slice(0, 10)}
                            onChange={(event) =>
                              setPaymentDate(event.target.value)
                            }
                            className="form-control"
                            style={{
                              border: "1px solid #d9d4cd",
                              borderRadius: "9px",
                              padding: "10px 12px",
                              fontSize: "13px",
                              boxShadow: "none",
                            }}
                            disabled={
                              !canEdit ||
                              (isExistingPayment && !editingExistingPayment)
                            }
                          />
                        </div>
                        <div className="col-md-4">
                          <label
                            htmlFor="transactionId"
                            className="form-label"
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#514d57",
                            }}
                          >
                            Transaction ID
                          </label>
                          <input
                            id="transactionId"
                            value={transactionId}
                            onChange={(event) =>
                              setTransactionId(event.target.value)
                            }
                            className="form-control"
                            placeholder="Enter transaction ID"
                            style={{
                              border: "1px solid #d9d4cd",
                              borderRadius: "9px",
                              padding: "10px 12px",
                              fontSize: "13px",
                              boxShadow: "none",
                            }}
                            disabled={
                              !canEdit ||
                              (isExistingPayment && !editingExistingPayment)
                            }
                          />
                        </div>
                        <div className="col-md-4">
                          <label
                            htmlFor="amountPaid"
                            className="form-label"
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#514d57",
                            }}
                          >
                            Amount Paid
                          </label>
                          <div style={{ position: "relative" }}>
                            <span
                              style={{
                                position: "absolute",
                                left: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#77727d",
                                zIndex: 1,
                              }}
                            >
                              PKR
                            </span>
                            <input
                              id="amountPaid"
                              value={amountPaid}
                              onChange={(event) =>
                                setAmountPaid(event.target.value)
                              }
                              className="form-control"
                              placeholder="0"
                              style={{
                                border: "1px solid #d9d4cd",
                                borderRadius: "9px",
                                padding: "10px 12px 10px 42px",
                                fontSize: "13px",
                                boxShadow: "none",
                              }}
                              disabled={
                                !canEdit ||
                                (isExistingPayment && !editingExistingPayment)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {canEdit && (
                      <div
                        className="mt-4 d-flex justify-content-end flex-wrap gap-2"
                      >
                        <button
                          type="button"
                          className="btn"
                          onClick={() =>
                            setShowWriteOffControls((prev) => !prev)
                          }
                          style={{
                            backgroundColor: showWriteOffControls
                              ? "#f9e1be"
                              : "#fff",
                            border: showWriteOffControls
                              ? "1px solid #f0d9a8"
                              : "1px solid #ddd9f7",
                            color: "#302c40",
                            borderRadius: "9px",
                            padding: "10px 18px",
                            fontWeight: "700",
                          }}
                        >
                          <i
                            className={`fa-solid ${showWriteOffControls ? "fa-eye-slash" : "fa-eye"}`}
                            style={{
                              marginRight: "7px",
                              color: "#302c40",
                            }}
                          />
                          {showWriteOffControls
                            ? "Hide clear remaining due"
                            : "Show clear remaining due"}
                        </button>
                        {isExistingPayment ? (
                          <button
                            type="button"
                            className="btn"
                            onClick={() =>
                              setEditingExistingPayment((prev) => !prev)
                            }
                            style={{
                              backgroundColor: editingExistingPayment
                                ? "#d5d1fd"
                                : "#fff",
                              border: "none",
                              color: "#302c40",
                              borderRadius: "9px",
                              padding: "10px 18px",
                              fontWeight: "700",
                            }}
                          >
                            <i
                              className={`fa-solid ${editingExistingPayment ? "fa-xmark" : "fa-pen"}`}
                              style={{
                                marginRight: "7px",
                                color: "#302c40",
                              }}
                            />
                            {editingExistingPayment
                              ? "Exit Edit Mode"
                              : "Edit Existing Payment"}
                          </button>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ ...cardStyle, marginTop: "20px", padding: "20px" }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6
                    style={{ margin: 0, fontWeight: "700", color: "#34313a" }}
                  >
                    Fee Coverage
                  </h6>
                  <small style={{ color: "#77727d", fontSize: "12px" }}>
                    {isExistingPayment
                      ? "Review or change the course months this payment covers."
                      : "Select the course months this payment covers."}
                  </small>
                </div>
                {selectedItems.length > 0 && (
                  <span
                    style={{
                      backgroundColor: "#bff1db",
                      color: "#315d4b",
                      borderRadius: "20px",
                      padding: "6px 11px",
                      fontSize: "12px",
                      fontWeight: "700",
                    }}
                  >
                    Allocated: PKR {totalAllocated.toLocaleString()}
                  </span>
                )}
              </div>
              {loadingPeriods || loadingExisting ? (
                <div style={{ color: "#77727d", fontSize: "13px" }}>
                  Loading {loadingExisting ? "transaction" : "fee periods"}...
                </div>
              ) : coursesSorted.length === 0 ? (
                <div style={{ color: "#77727d", fontSize: "13px" }}>
                  No fee periods are available for this registration.
                </div>
              ) : (
                <div className="row g-3">
                  {coursesSorted.map((course) => (
                    <div key={course.courseId} className="col-md-4">
                      <div
                        style={{
                          border: "1px solid #eeeae5",
                          borderRadius: "12px",
                          padding: "14px",
                          height: "100%",
                        }}
                      >
                        {(() => {
                          const pricing = coursePricingById[String(course.courseId)];
                          const hasDiscount = pricing && pricing.discount > 0;
                          return (
                            <>
                              <h6
                                style={{
                                  fontWeight: "700",
                                  fontSize: "14px",
                                  color: "#403b52",
                                  marginBottom: hasDiscount ? "4px" : "12px",
                                }}
                              >
                                {course.courseName}
                              </h6>
                              {hasDiscount ? (
                                <small
                                  className="d-block"
                                  style={{
                                    color: "#77727d",
                                    fontSize: "11px",
                                    marginBottom: "12px",
                                    lineHeight: 1.35,
                                  }}
                                >
                                  PKR {pricing.fee.toLocaleString()} − discount{" "}
                                  PKR {pricing.discount.toLocaleString()} ={" "}
                                  <span
                                    style={{ color: "#403b52", fontWeight: 600 }}
                                  >
                                    PKR {pricing.net.toLocaleString()}/mo
                                  </span>
                                </small>
                              ) : null}
                            </>
                          );
                        })()}
                        {course.periods.map((period) => {
                          const remaining = remainingForPeriod(period);
                          const wasAllocatedHere =
                            existingAllocations[period.feePeriodId] != null;
                          const isFocPending =
                            period.status === "unpaid" &&
                            Number(period.amountDue) <= 0 &&
                            remaining <= 0;
                          const isFocOnThisTxn =
                            wasAllocatedHere &&
                            Number(period.amountDue) <= 0 &&
                            Number(period.amountPaid || 0) <= 0;
                          // While editing an existing txn, keep its allocations
                          // selectable (incl. settled FOC) so they can be removed.
                          const selectable =
                            period.status !== "waived" &&
                            ((period.status !== "paid" &&
                              (remaining > 0 || isFocPending)) ||
                              (editingExistingPayment && wasAllocatedHere));
                          const checked = (
                            selectedPeriods[course.courseId] || []
                          ).some(
                            (item) => item.feePeriodId === period.feePeriodId,
                          );
                          const statusHint =
                            period.status === "waived"
                              ? "Waived"
                              : isFocPending || isFocOnThisTxn
                                ? isFocPending
                                  ? "FOC – pending verification"
                                  : "FOC – on this payment"
                                : period.status === "paid"
                                  ? "Paid"
                                  : `Remaining: PKR ${Number(remaining).toLocaleString()}`;
                          return (
                            <div
                              key={period.feePeriodId}
                              style={{
                                backgroundColor: checked
                                  ? "#f4f1ff"
                                  : "#faf9f7",
                                borderRadius: "9px",
                                padding: "9px 10px",
                                marginBottom: "8px",
                                opacity: selectable ? 1 : 0.65,
                              }}
                            >
                              <label
                                className="form-check"
                                style={{
                                  cursor: selectable ? "pointer" : "default",
                                  margin: 0,
                                }}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  disabled={
                                    !canEdit ||
                                    (isExistingPayment && !editingExistingPayment) ||
                                    !selectable
                                  }
                                  checked={checked}
                                  onChange={() =>
                                    togglePeriod(course.courseId, period)
                                  }
                                />
                                <span style={{ flex: 1 }}>
                                  <strong
                                    style={{
                                      fontSize: "13px",
                                      color: "#34313a",
                                    }}
                                  >
                                    {period.label}
                                  </strong>
                                  <small
                                    className="d-block"
                                    style={{
                                      color: "#77727d",
                                      fontSize: "11px",
                                    }}
                                  >
                                    {statusHint}
                                  </small>
                                </span>
                              </label>
                              {checked && (
                                <input
                                  type="number"
                                  min="0"
                                  max={
                                    isFocPending || isFocOnThisTxn
                                      ? 0
                                      : remaining
                                  }
                                  value={
                                    allocationAmounts[period.feePeriodId] ??
                                    remaining
                                  }
                                  onChange={(event) =>
                                    setAllocationAmounts((current) => ({
                                      ...current,
                                      [period.feePeriodId]:
                                        isFocPending || isFocOnThisTxn
                                          ? 0
                                          : event.target.value,
                                    }))
                                  }
                                  disabled={
                                    !canEdit || isFocPending || isFocOnThisTxn
                                  }
                                  className="form-control form-control-sm mt-2"
                                  style={{ borderRadius: "7px" }}
                                />
                              )}
                              {canEdit &&
                                showWriteOffControls &&
                                remaining > 0 &&
                                Number(period.amountDue) >
                                  Number(period.amountPaid || 0) && (
                                  <button
                                    type="button"
                                    className="btn btn-sm mt-2"
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      borderRadius: "7px",
                                      backgroundColor: "#f9e1be",
                                      borderColor: "#f0d9a8",
                                      color: "#8a5a00",
                                    }}
                                    onClick={() => {
                                      notificationCtx.show({
                                        message: `Clear remaining due for ${period.label}: set amount due equal to amount paid (PKR ${Number(period.amountPaid || 0).toLocaleString()}). Registration balance will decrease. No cash or teacher change. Continue?`,
                                        stat: "info",
                                        onConfirm: async () => {
                                          try {
                                            await registrationsApi.writeOffResidual(
                                              registrationKey,
                                              period.feePeriodId,
                                            );
                                            notificationCtx.show({
                                              message:
                                                "Remaining due cleared successfully",
                                              stat: "success",
                                            });
                                            setLoadingPeriods(true);
                                            try {
                                              const response =
                                                await feeTransactionsApi.feePeriods(
                                                  registrationKey,
                                                );
                                              setCourses(response.data || []);
                                              if (typeof onSuccess === "function") {
                                                onSuccess();
                                              }
                                            } finally {
                                              setLoadingPeriods(false);
                                            }
                                          } catch (error) {
                                            notificationCtx.show({
                                              message:
                                                error?.response?.data
                                                  ?.message ||
                                                "Failed to clear remaining due.",
                                              stat: "error",
                                            });
                                          }
                                        },
                                      });
                                    }}
                                  >
                                    Clear remaining due
                                  </button>
                                )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div
            className="modal-footer"
            style={{
              border: "none",
              backgroundColor: "#ffffff",
              padding: "0 24px 24px",
            }}
          >
            <button
              className="btn"
              onClick={close}
              style={{
                backgroundColor: "#fff",
                border: "none",
                color: "#514d57",
                borderRadius: "9px",
                padding: "10px 18px",
                fontWeight: "600",
              }}
            >
              Cancel
            </button>
            {canEdit && (
              <button
                className="btn"
                onClick={isExistingPayment ? updateExistingPayment : submit}
                disabled={
                  submitting ||
                  !transactionId ||
                  !paymentDate ||
                  amountPaid === "" ||
                  amountPaid == null ||
                  selectedItems.length === 0 ||
                  Number(amountPaid) !== totalAllocated ||
                  (isExistingPayment && !existingTransaction?.id) ||
                  (isExistingPayment && !editingExistingPayment)
                }
                style={{
                  backgroundColor: "#d5d1fd",
                  border: "none",
                  color: "#302c40",
                  borderRadius: "9px",
                  padding: "10px 18px",
                  fontWeight: "700",
                }}
              >
                <i
                  className={`fa-solid ${isExistingPayment ? "fa-pen-to-square" : "fa-circle-check"}`}
                  style={{ marginRight: "7px" }}
                />
                {submitting
                  ? "Working..."
                  : isExistingPayment
                    ? "Update Payment"
                    : "Verify Payment"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeeVerificationModal;
